import React from "react";
import * as d3 from "d3";
import { convertToMillion, formatThousands, convertToThousands } from "utils/Utils";

class DoughnutChart extends React.Component {
  constructor(props) {
    super(props);
    this.chartRef = React.createRef();
  }

  componentDidMount() {
    this.drawChart(this.props);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.data !== this.props.data ||
      prevProps.type !== this.props.type ||
      prevProps.colors !== this.props.colors ||
      prevProps.fixedColors !== this.props.fixedColors
    ) {
      this.drawChart(this.props);
    }
  }

  drawChart = (props) => {
    if (!props.data || props.data.length === 0) return;

    const container = this.chartRef.current;
    d3.select(container).selectAll("svg").remove();

    const data = props.data;

    const formatValue = (v) => {
      const fmt = (props.valueFormat || props.type || "").toLowerCase();
      if (fmt === "m" || fmt === "million") return `${convertToMillion(v)} M`;
      if (fmt === "k" || fmt === "thousand" || fmt === "thousands") return `${convertToThousands(v)} K`;
      if (fmt === "comma" || fmt === "number") return formatThousands(v);
      return d3.format(",")(v);
    };

    const formatThousandsNoDecimal = (num) => {
      if (num === null || num === undefined || num === "") return "";
      const n = typeof num === "number" ? num : parseNumeric(num);
      return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const parseNumeric = (val) => {
      if (val === null || val === undefined || val === "") return 0;
      if (typeof val === "number") return val;
      let s = String(val).trim();
      if (s === "") return 0;
      s = s.replace(/[%$,\s\u00A0]/g, "");
      s = s.replace(/[\u00A3\u00A5\u20AC\u20B9\u20A9\u20AB\u20B4\u20BD\u20BA\u20AA\u0E3F\u066B\u060C]/g, "");
      if (/^\(.+\)$/.test(s)) s = "-" + s.replace(/[()]/g, "");
      const n = Number(s);
      return isNaN(n) ? 0 : n;
    };

    // base canvas size (outer visible area)
    const width = props.width || 480;
    const height = props.height || 480;
    const radius = Math.min(width, height) / 2;

    // color palette & stable color mapping by name
    let palette = Array.isArray(props.colors) ? props.colors.slice() : null;
    if (!palette || palette.length === 0) {
      palette = ['#7858FF','#36A41D','#FA4F96','#049F9A','#DFC425','#FFD700'].concat(d3.schemeCategory10 || []);
    }
    if (palette.length < data.length) {
      palette = new Array(data.length).fill(null).map((_, i) => palette[i % palette.length]);
    }
    const fixedColors = (props.fixedColors && typeof props.fixedColors === "object") ? props.fixedColors : {};
    const allNames = Array.from(new Set(data.map(d => d.field_name)));
    const sortedNames = allNames.slice().sort((a,b) => (String(a)).localeCompare(String(b)));
    const fallbackMap = {};
    for (let i = 0; i < sortedNames.length; i++) {
      const nm = sortedNames[i];
      fallbackMap[nm] = palette[i % palette.length];
    }
    const colorForName = (name, index) => {
      if (fixedColors.hasOwnProperty(name) && fixedColors[name]) return fixedColors[name];
      if (fallbackMap.hasOwnProperty(name)) return fallbackMap[name];
      return palette[index % palette.length];
    };

    // initial svg viewBox = width x height (we will expand vertically if labels overflow)
    const svg = d3.select(container)
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .style("max-width", `${width}px`)
      .style("max-height", `${height}px`)
      .style("display", "block")
      .style("margin", "0 auto")
      .style("overflow", "visible");

    // group centered in the initial viewBox
    const g = svg.append("g").attr("transform", `translate(${width / 2}, ${height / 2})`);

    const pie = d3.pie().sort(null).value(d => d.field_value);
    const arc = d3.arc().outerRadius(radius - 10).innerRadius(radius * 0.6);
    const outerArc = d3.arc().innerRadius(radius * 1.05).outerRadius(radius * 1.05);

    const pieData = pie(data);

    // draw slices
    const arcs = g.selectAll(".arc")
      .data(pieData)
      .enter()
      .append("g")
      .attr("class", "arc");

    arcs.append("path")
      .attr("d", arc)
      .attr("fill", (d, i) => colorForName(d.data.field_name, i));

    // ------------------ LABELS: measure first, then simulate with real sizes ------------------
    const padding = 3; // requested padding around each label
    const baseOffset = radius * 1.35;

    // temporary group for creating text to measure bbox (positioned at group's center)
    const measureGroup = svg.append("g").attr("class", "measure-group").attr("transform", `translate(${width/2}, ${height/2})`);

    // create temp label nodes to measure
    const tempLabelGroups = measureGroup.selectAll(".tempLabel")
      .data(pieData)
      .enter()
      .append("g")
      .attr("class", "tempLabel");

    tempLabelGroups.each(function(d, i) {
      const gTemp = d3.select(this);
      const total = d3.sum(data.map(dd => dd.field_value));
      const percent = total ? ((d.data.field_value / total) * 100).toFixed(1) + "%" : "0%";
      const valueStr = formatValue(d.data.field_value);
      const nameLine = String(d.data.field_name || "");
      const valueLine = `${d.data.currency_symbol || ""} ${formatThousandsNoDecimal(valueStr)} (${percent})`;
      const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
      const anchor = (midangle < Math.PI ? "start" : "end");

      const txt = gTemp.append("text").attr("text-anchor", anchor);
      txt.append("tspan")
        .attr("x", 0)
        .attr("dy", 0)
        .style("font-size", "20px")
        .style("font-weight", "600")
        .text(nameLine);

      txt.append("tspan")
        .attr("x", 0)
        .attr("dy", "1.2em")
        .style("font-size", "19px")
        .style("font-weight", "500")
        .text(valueLine);
    });

    // Build measured nodes array
    const measuredNodes = [];
    tempLabelGroups.nodes().forEach((node, i) => {
      const d = pieData[i];
      const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
      const side = (midangle < Math.PI) ? 1 : -1;
      const labelX = baseOffset * side;
      const pos = outerArc.centroid(d);
      const anchor = (midangle < Math.PI ? "start" : "end");

      // measure (fallback sensible defaults)
      let bb = { x: 0, y: 0, width: 60, height: 28 };
      try {
        const textNode = d3.select(node).select("text").node();
        if (textNode) bb = textNode.getBBox();
      } catch (e) {
        // keep fallback
      }

      measuredNodes.push({
        index: i,
        x: labelX,
        y: pos[1],
        side,
        midangle,
        width: bb.width,
        height: bb.height,
        anchor,
        rawPosY: pos[1] // keep original for grouping identical positions
      });
    });

    // cleanup measurement group
    measureGroup.remove();

    // If multiple labels share nearly the same starting y (very common when value=0),
    // pre-spread them a bit so they don't all start at the exact same coordinate.
    // Group by rounded rawPosY
    const groups = {};
    const roundTo = 0.5; // precision for grouping (0.5 px)
    measuredNodes.forEach(n => {
      const key = Math.round(n.rawPosY / roundTo) * roundTo;
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    });

    Object.values(groups).forEach(group => {
      if (group.length > 1) {
        // sort by index for deterministic spread
        group.sort((a,b) => a.index - b.index);
        const n = group.length;
        // compute average height for gap estimation
        const avgH = Math.max(20, d3.mean(group, g => g.height || 28));
        const gap = avgH + padding + 6; // extra gap for zero-value collisions
        // center spread around original y
        const centerY = d3.mean(group, g => g.rawPosY);
        for (let i = 0; i < n; i++) {
          const offset = (i - (n - 1) / 2) * gap;
          group[i].y = centerY + offset;
          // nudge x slightly outward if many on same spot to avoid perfectly vertical stack
          group[i].x = group[i].x + (group[i].side * (Math.abs(i - (n - 1) / 2) * 4));
        }
      }
    });

    // run force simulation using measured heights for collision radius
    const simNodes = measuredNodes.map(n => Object.assign({}, n));
    // stronger collide extra when many zeros cause tough separation
    const collideExtra = 6; // increase if still touching
    const simulation2 = d3.forceSimulation(simNodes)
      .stop()
      .force("y", d3.forceY(d => d.y).strength(0.5))
      // collision uses half height + padding + collideExtra
      .force("collide", d3.forceCollide(d => (Math.max(20, d.height) / 2) + padding + collideExtra).iterations(4))
      .force("x", d3.forceX(d => d.side * baseOffset).strength(1.0));

    // ticks - increase ticks for stability
    const ticks = 900; // increased from 600 to make simulation more stable for tricky datasets
    for (let t = 0; t < ticks; ++t) simulation2.tick();
    simulation2.stop();

    const labelPosByIndex = {};
    simNodes.forEach(n => {
      labelPosByIndex[n.index] = { x: n.x, y: n.y, side: n.side, midangle: n.midangle, width: n.width, height: n.height, anchor: n.anchor };
    });

    // draw polylines (connectors) using final label positions
    g.selectAll("allPolylines")
      .data(pieData)
      .enter()
      .append("polyline")
      .attr("stroke", (d, i) => colorForName(d.data.field_name, i))
      .style("fill", "none")
      .attr("stroke-width", 1.4)
      .attr("points", function(d, i) {
        const posA = arc.centroid(d);
        const posB = outerArc.centroid(d);
        const lp = labelPosByIndex[i];
        const posC = lp ? [lp.x, lp.y] : posB;
        return [posA, posB, posC];
      });

    // draw labels as groups with background rect (3px padding)
    const labelGroups = g.selectAll(".labelGroup")
      .data(pieData)
      .enter()
      .append("g")
      .attr("class", "labelGroup")
      .attr("transform", (d, i) => {
        const lp = labelPosByIndex[i] || outerArc.centroid(d);
        return `translate(${lp.x},${lp.y})`;
      });

    // create text and padded rect behind it
    labelGroups.each(function(d, i) {
      const lp = labelPosByIndex[i] || { anchor: "start", width: 60, height: 28, side: 1 };
      const gLabel = d3.select(this);
      const txt = gLabel.append("text").attr("text-anchor", lp.anchor);

      const total = d3.sum(data.map(dd => dd.field_value));
      const percent = total ? ((d.data.field_value / total) * 100).toFixed(1) + "%" : "0%";
      const valueStr = formatValue(d.data.field_value);
      const nameLine = String(d.data.field_name || "");
      const valueLine = `${d.data.currency_symbol || ""} ${formatThousandsNoDecimal(valueStr)} (${percent})`;

      txt.append("tspan")
        .attr("x", 0)
        .attr("dy", 0)
        .style("font-size", "20px")
        .style("font-weight", "600")
        .text(nameLine);

      txt.append("tspan")
        .attr("x", 0)
        .attr("dy", "1.2em")
        .style("font-size", "19px")
        .style("font-weight", "500")
        .text(valueLine);

      // measure and insert padded rect behind text
      try {
        const bb = txt.node().getBBox();
        gLabel.insert("rect", "text")
          .attr("x", bb.x - padding)
          .attr("y", bb.y - padding)
          .attr("width", bb.width + padding * 2)
          .attr("height", bb.height + padding * 2)
          .attr("fill", "transparent");
      } catch (e) {
        // fallback if getBBox fails - rough estimate
        const w = lp.width || 80;
        const h = lp.height || 28;
        const x = lp.anchor === "end" ? -w - padding : -padding;
        const y = -h / 2 - padding;
        gLabel.insert("rect", "text")
          .attr("x", x)
          .attr("y", y)
          .attr("width", w + padding * 2)
          .attr("height", h + padding * 2)
          .attr("fill", "transparent");
      }
    });

    // marker circles (aligned to final positions)
    g.selectAll("labelMarkers")
      .data(pieData)
      .enter()
      .append("circle")
      .attr("cx", (d, i) => {
        const lp = labelPosByIndex[i];
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        const shift = midangle < Math.PI ? -12 : 12;
        return lp ? (lp.x + shift) : 0;
      })
      .attr("cy", (d, i) => (labelPosByIndex[i] ? labelPosByIndex[i].y : 0))
      .attr("r", 6)
      .attr("fill", (d, i) => colorForName(d.data.field_name, i));

    // --- DYNAMIC VERTICAL PADDING ADJUSTMENT (same logic as before) ---
    try {
      const textNodes = g.selectAll("text").nodes();
      if (textNodes && textNodes.length > 0) {
        let minY = Infinity, maxY = -Infinity;
        textNodes.forEach((tn) => {
          const bb = tn.getBBox();
          minY = Math.min(minY, bb.y);
          maxY = Math.max(maxY, bb.y + bb.height);
        });

        const topVisible = -height / 2;
        const bottomVisible = height / 2;
        const margin = 12;

        const extraTop = Math.max(0, (topVisible + margin) - minY);
        const extraBottom = Math.max(0, maxY - (bottomVisible - margin));

        if (extraTop > 0 || extraBottom > 0) {
          const newViewHeight = Math.round(height + extraTop + extraBottom);
          svg.attr("viewBox", `0 0 ${width} ${newViewHeight}`)
             .style("max-height", `${newViewHeight}px`);
          g.attr("transform", `translate(${width / 2}, ${height / 2 + extraTop})`);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Label bbox measurement failed, skipping auto-padding", err);
    }

    simulation2.force(null);
  };

  render() {
    return <div ref={this.chartRef} className="doughnutChartMarginTop" />;
  }
}

export default DoughnutChart;
